'use client'

import { PopperContentProps } from '@radix-ui/react-popper'
import * as React from 'react'

import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'

import { Label } from './ui/label'

export interface SelectOption {
  label: string
  value: string
}
export interface AppSelectProps {
  label?: string
  placeholder?: string
  options: SelectOption[]
  value: string
  onChange(value: string): void
  className?: string
  disabled?: boolean
  triggerClassName?: string
  selectValueComponent?: React.ReactNode
  menuPlacement?: PopperContentProps['side']
  avoidCollisions?: boolean
  selectContentClassName?: string
  required?: boolean
  dir?: 'rtl' | 'ltr'
  defaultValue?: string
}
export const AppSelect = React.forwardRef<HTMLButtonElement, AppSelectProps>(
  (
    {
      label,
      placeholder,
      options,
      value,
      onChange,
      triggerClassName,
      className,
      disabled,
      selectValueComponent,
      menuPlacement,
      avoidCollisions,
      selectContentClassName,
      required,
      dir = 'rtl',
      defaultValue,
    },
    ref
  ) => {
    // Simply ensure value is a string
    const safeValue = value || ''

    return (
      <div dir={dir} className={cn('relative', className)}>
        {label && (
          <Label className='text-text-primary text-sm'>
            {label}
            {required && <span className='text-gray-400'>*</span>}
          </Label>
        )}
        <Select defaultValue={defaultValue} value={safeValue} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger
            ref={ref}
            className={cn(
              'text-text-secondary h-full w-full cursor-pointer rounded-full border bg-white !px-3 py-2 shadow-none',
              triggerClassName
            )}
          >
            {selectValueComponent || <SelectValue placeholder={label ? '' : placeholder || ''} />}
          </SelectTrigger>
          <SelectContent
            side={menuPlacement}
            className={cn('overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md', selectContentClassName)}
            avoidCollisions={avoidCollisions}
          >
            <SelectGroup dir={dir}>
              {options.map(option => (
                <SelectItem key={option.value} value={option.value} className='break-words whitespace-normal'>
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    )
  }
)

AppSelect.displayName = 'AppSelect'
